<template>
  <b-modal
    :visible="show"
    :title="titleText"
    centered
    no-close-on-backdrop
    @hide="$emit('close')"
  >
    <!-- Success -->
    <div v-if="success">
      <b-alert show variant="success" class="mb-3">
        Hold {{ action === 'confirm' ? 'confirmed' : 'reversed' }} successfully.
      </b-alert>

      <dl class="row mb-0">
        <dt class="col-5 text-muted">Hold ID</dt>
        <dd class="col-7 text-break">
          {{ fromResultOrHold('hold_id') }}
          <i class="fa fa-copy ml-1 cursor-pointer" @click="copy(fromResultOrHold('hold_id'), 'Hold ID')"></i>
        </dd>

        <dt class="col-5 text-muted">Wallet ID</dt>
        <dd class="col-7 text-break">
          {{ fromResultOrHold('wallet_id') }}
          <i class="fa fa-copy ml-1 cursor-pointer" @click="copy(fromResultOrHold('wallet_id'), 'Wallet ID')"></i>
        </dd>

        <dt class="col-5 text-muted">Amount</dt>
        <!-- currency_code isn't on the confirm/reverse response, so it comes from the hold -->
        <dd class="col-7">{{ formatAmount(fromResultOrHold('amount')) }} {{ hold && hold.currency_code }}</dd>

        <dt class="col-5 text-muted">New status</dt>
        <dd class="col-7">{{ result.status || '—' }}</dd>

        <!-- Confirm-only fields -->
        <template v-if="action === 'confirm'">
          <dt class="col-5 text-muted">Transaction ID</dt>
          <dd class="col-7 text-break">
            <span v-if="result.transaction_id">
              {{ result.transaction_id }}
              <i class="fa fa-copy ml-1 cursor-pointer" @click="copy(result.transaction_id, 'Transaction ID')"></i>
            </span>
            <span v-else>—</span>
          </dd>
          <dt class="col-5 text-muted">Balance after</dt>
          <dd class="col-7">{{ formatAmount(result.balance_after) }}</dd>
          <dt class="col-5 text-muted">Available after</dt>
          <dd class="col-7">{{ formatAmount(result.available_balance_after) }}</dd>
        </template>

        <!-- Reverse-only fields -->
        <template v-else>
          <dt class="col-5 text-muted">Reversed at</dt>
          <dd class="col-7">{{ formatUtcDateTime(result.reversed_at) }}</dd>
          <dt class="col-5 text-muted">Available after</dt>
          <dd class="col-7">{{ formatAmount(result.available_balance_after) }}</dd>
        </template>
      </dl>
    </div>

    <!-- Failure -->
    <div v-else>
      <b-alert show variant="danger" class="mb-3">
        <strong>{{ action === 'confirm' ? 'Confirm' : 'Reverse' }} failed.</strong>
        <div class="mt-1">{{ errorMessage }}</div>
        <div v-if="statusCode" class="small mt-1">HTTP {{ statusCode }}</div>
      </b-alert>

      <dl v-if="hold" class="row mb-0">
        <dt class="col-5 text-muted">Hold ID</dt>
        <dd class="col-7 text-break">{{ hold.hold_id }}</dd>
        <dt class="col-5 text-muted">Wallet ID</dt>
        <dd class="col-7 text-break">{{ hold.wallet_id }}</dd>
        <dt class="col-5 text-muted">Amount</dt>
        <dd class="col-7">{{ formatAmount(hold.amount) }} {{ hold.currency_code }}</dd>
      </dl>
    </div>

    <template #modal-footer>
      <b-button variant="outline-secondary" @click="$emit('close')">Close</b-button>
      <!-- Retry reuses the original idempotency key (held by the parent) so the
           wallet never double-debits / double-releases. -->
      <b-button v-if="!success" variant="primary" :disabled="loading" @click="$emit('retry')">
        <span v-if="loading">Retrying…</span>
        <span v-else>Retry</span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { formatAmount, formatUtcDateTime } from "@/utils/holds/formatters";

export default {
  name: "HoldResultModal",
  props: {
    show: { type: Boolean, default: false },
    action: { type: String, default: "confirm" }, // 'confirm' | 'reverse'
    success: { type: Boolean, default: false },
    result: { type: Object, default: () => ({}) },
    error: { type: Object, default: null },
    hold: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  computed: {
    titleText() {
      const verb = this.action === "confirm" ? "Confirm" : "Reverse";
      return this.success ? `${verb} result` : `${verb} failed`;
    },
    errorMessage() {
      if (!this.error) return "An unexpected error occurred.";
      // The backend surfaces the underlying wallet error as `wallet_message`; fall back
      // to the arch message/detail, then a generic line.
      const data = this.error.data || {};
      return (
        data.wallet_message ||
        data.message ||
        data.detail ||
        this.error.message ||
        "An unexpected error occurred."
      );
    },
    statusCode() {
      // Prefer the wallet API's status (the real cause); fall back to the HTTP status.
      const data = this.error && this.error.data;
      return (data && data.wallet_status) || (this.error && this.error.status) || null;
    },
  },
  methods: {
    formatAmount,
    formatUtcDateTime,
    // Prefer the action result's value, falling back to the source hold. Uses an
    // explicit null/undefined check (not ||) so a legitimate 0 isn't discarded.
    fromResultOrHold(field) {
      if (this.result && this.result[field] !== undefined && this.result[field] !== null) {
        return this.result[field];
      }
      return this.hold ? this.hold[field] : undefined;
    },
    copy(value, label) {
      if (!value) return;
      const done = () => {
        if (this.$toast) this.$toast.open({ message: `${label} copied`, type: "success", duration: 2000, position: "top-right" });
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(done).catch(() => {});
      } else {
        const el = document.createElement("textarea");
        el.value = value;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        done();
      }
    },
  },
};
</script>
